import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { symbolData } from "../SymbolData";
import { FEATURES_CONFIG, winLoops } from "../featuresConfig";

/**
 * SpineAnimationData
 *
 * Centralized cache for spine animation metadata (durations, names).
 * Reads from spine skeleton data once at initialization, then provides
 * instant lookups for all consumers (win controller, overlay controller, etc).
 *
 * Singleton pattern — same as SpineTextureExtractor.
 *
 * Usage:
 *   SpineAnimationData.initialize();         // Call once after assets loaded
 *   SpineAnimationData.getMasterWinDuration();  // e.g. 2000 (ms)
 *   SpineAnimationData.getFastWinDuration();    // e.g. 1000 (ms) or null
 *   SpineAnimationData.getWinBoxDuration();     // e.g. 1000 (ms)
 */
class SpineAnimationData {
  static _instance = null;

  // ── Symbol pay type priority (highest = master duration source) ──
  static SYMBOL_PRIORITY = {
    wild: 4,
    scatter: 3,
    hp: 2,
    mp: 1,
    lp: 0,
  };

  /**
   * One-time initialization. Call once after PIXI assets are loaded.
   * Reads all spine animation durations and caches them.
   */
  static initialize() {
    if (SpineAnimationData._instance) return;
    SpineAnimationData._instance = new SpineAnimationData();
    SpineAnimationData._instance._extractAll();
  }

  // ── Public getters (all return ms or null) ──

  /**
   * Master "win" animation duration from the highest-priority symbol.
   * Already scaled by DURATION_SCALE from config.
   * @returns {number} Duration in ms
   */
  static getMasterWinDuration() {
    return SpineAnimationData._instance?._cache.masterWin
      ?? FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION;
  }

  /**
   * "win_fast" animation duration from the highest-priority symbol.
   * Already scaled by DURATION_SCALE from config.
   * @returns {number|null} Duration in ms, or null if win_fast not available
   */
  static getFastWinDuration() {
    return SpineAnimationData._instance?._cache.fastWin ?? null;
  }

  /**
   * How long the GLOBAL WIN is held — the "show all wins" beat.
   *
   * One master win loop times two, or one in turbo. This is the pause the
   * player already reads as "the win is being shown", and it is the only timing
   * in the game that means that — so anything else waiting on the same idea
   * should ask for it here rather than pick a number that happens to look
   * similar.
   *
   * Extracted from useFreeSpinBasicWinController, which computed it inline, so
   * the beat has ONE definition and cannot drift between the two callers.
   *
   * @param   {Object}  [p]
   * @param   {boolean} [p.useFastWin] This round is using the `win_fast` clip.
   * @param   {boolean} [p.turbo]      Turbo holds the global win for one loop.
   * @returns {number} Milliseconds.
   */
  static getShowAllWindowMs({ useFastWin = false, turbo = false } = {}) {
    const masterMs = SpineAnimationData.getMasterWinDuration();
    const fastMs = SpineAnimationData.getFastWinDuration();
    const normalLoops = turbo
      ? FEATURES_CONFIG.SHOW_ALL_WINS.LOOPS_FOR_SHOW_ALL_TURBO
      : FEATURES_CONFIG.SHOW_ALL_WINS.LOOPS_FOR_SHOW_ALL;
    const loops = winLoops(useFastWin, normalLoops);
    return (useFastWin && fastMs !== null ? fastMs : masterMs) * loops;
  }

  /** @returns {number|null} Win box "Win" animation duration in ms */
  static getWinBoxDuration() {
    return SpineAnimationData._instance?._cache.winBox ?? null;
  }

  /** @returns {number|null} Win lines animation duration in ms (from first win_line_*) */
  static getWinLineDuration() {
    return SpineAnimationData._instance?._cache.winLine ?? null;
  }

  /** @returns {number|null} WinText full animation duration in ms */
  static getWinTextDuration() {
    return SpineAnimationData._instance?._cache.winText ?? null;
  }

  /**
   * WinText animation chain duration (in + idle + out or idle + out)
   * @param {boolean} skipIntro - If true, returns idle + out only
   * @returns {number} Duration in ms, or 0 if not available
   */
  static getWinTextChainDuration(skipIntro = false) {
    if (!SpineAnimationData._instance) return 0;
    return skipIntro
      ? (SpineAnimationData._instance._cache.winTextChainSkipIntro ?? 0)
      : (SpineAnimationData._instance._cache.winTextChainFull ?? 0);
  }

  /**
   * Generic lookup: get any animation duration from any spine
   * @param {string} spineName - Spine asset name (e.g. "winbox", "wild")
   * @param {string} animationName - Animation name (e.g. "Win", "win_fast")
   * @returns {number|null} Duration in ms, or null if not found
   */
  static getAnimationDuration(spineName, animationName) {
    const key = `${spineName}:${animationName}`;
    return SpineAnimationData._instance?._animationDurations.get(key) ?? null;
  }

  /**
   * Per-symbol win animation duration lookup (O(1) — pre-cached at init).
   * Returns the actual win/win_fast duration for a specific symbol ID.
   * Used to compute per-symbol loop counts: Math.round(displayWindow / symbolDuration).
   *
   * @param {number} symbolId - Symbol ID from symbolData
   * @param {boolean} useFastWin - If true, returns win_fast duration (falls back to win if unavailable)
   * @returns {number|null} Duration in ms, or null if symbol has no spine/win animation
   */
  static getSymbolWinDuration(symbolId, useFastWin = false) {
    if (!SpineAnimationData._instance) return null;
    const symInfo = symbolData[symbolId];
    if (!symInfo?.spine) return null;
    // ── Measure the rig that actually PLAYS the win, not the one on the reel ──
    //
    // Normally they are the same skeleton and `winSpine` is absent. The mystery
    // is the exception: its cell claims symbol 9 for its whole life, but symbol
    // 9 is `my` — a LID, with no `win` clip at all. A Golden Mystery cell
    // celebrates on the frame UNDER that lid (GOLDEN_FRAME_SYMBOL_ID, the old
    // `my`), and measuring the lid returned null, which collapsed the loop
    // count to 1: the coins bounced once while every other symbol on the same
    // win looped twice. Broke 2026-09-09 with the two-layer rebuild, found
    // 2026-09-10.
    //
    // An ordinary mystery is unaffected either way — with no win clip it plays
    // nothing, so how many times it would have looped never comes up.
    const spineName = symInfo.winSpine || symInfo.spine;
    const durations = SpineAnimationData._instance._spineWinDurations.get(spineName);
    if (!durations) return null;
    if (useFastWin && durations.winFast !== null) return durations.winFast;
    return durations.win;
  }

  /**
   * Tension spine cycle duration lookup (O(1) — pre-cached at init).
   * Returns in + idle + out duration from the tension spine.
   * Used by ReelControllerV2 to derive deceleration timing from ratios.
   * @returns {number|null} Duration in ms, or null if tension spine not found
   */
  static getTensionCycleDuration() {
    return SpineAnimationData._instance?._cache.tensionCycle ?? null;
  }

  /**
   * The longest LANDING clip across every symbol spine, in ms.
   *
   * The stall guard for "wait until the board is still" — see useWinController.
   * Read from the loaded spine data rather than written down, so a re-export
   * that lengthens a bounce moves the guard with it. Mad Labs today: 367ms
   * (scg) to 667ms (bs, sc, mul).
   *
   * Landing clip names vary per spine and this matches the same three rules
   * OverlayReelController.getAvailableAnimations uses — "land", "bounce", and
   * exactly "in" ("in" as a substring would match "win").
   *
   * @returns {number|null} ms, or null before the cache is built.
   */
  static getMaxLandingDuration() {
    const inst = SpineAnimationData._instance;
    if (!inst) return null;
    let max = 0;
    for (const [key, ms] of inst._animationDurations) {
      const clip = key.slice(key.indexOf(":") + 1).toLowerCase();
      if (clip.includes("land") || clip.includes("bounce") || clip === "in") {
        if (ms > max) max = ms;
      }
    }
    return max > 0 ? max : null;
  }

  /** @returns {boolean} Whether the cache has been initialized */
  static isReady() {
    return SpineAnimationData._instance !== null;
  }

  constructor() {
    this._cache = {
      masterWin: null,
      fastWin: null,
      winBox: null,
      winLine: null,
      winText: null,
      winTextChainFull: null,     // in + idle + out (ms)
      winTextChainSkipIntro: null, // idle + out (ms)
      tensionCycle: null,          // tension in + idle + out (ms)
    };
    // Generic map for any spine:animation → duration (ms)
    this._animationDurations = new Map();
    // Per-spine win duration cache: spineName → { win: ms, winFast: ms|null }
    // Built once at init, used for per-symbol loop count computation
    this._spineWinDurations = new Map();
  }

  /**
   * Extract all animation durations from spine data.
   */
  _extractAll() {
    // ── Win symbol animations (master + fast) ──
    this._extractWinDurations();

    // ── Win Box ──
    // this._cache.winBox = this._probeSpineDuration("win_box", "Win");
    // Midnight Alchemy's export names the clip `win` (Zeus: `Win`).
    this._cache.winBox =
      this._probeSpineDuration("winbox", "win") ??
      this._probeSpineDuration("winbox", "Win");

    // ── Win Lines (use first win_line_* animation) ──
    this._cache.winLine = this._probeFirstMatching("win_lines", "win_line_");

    // ── Win Text (winline_text) ──
    this._cache.winText = this._probeSpineDuration("winline_text", "in");
    this._extractWinTextChainDurations();

    // ── Tension spine (anticipation per-reel cycle) ──
    this._extractTensionCycleDuration();
  }

  /**
   * Extract win text animation chain durations (in + idle + out).
   * Cached once — replaces per-call spine data scanning in WinTextController.
   */
  _extractWinTextChainDurations() {
    const inDur = this._animationDurations.get("winline_text:in") ?? 0;
    const idleDur = this._animationDurations.get("winline_text:idle") ?? 0;
    const outDur = this._animationDurations.get("winline_text:out") ?? 0;
    this._cache.winTextChainFull = inDur + idleDur + outDur;
    this._cache.winTextChainSkipIntro = idleDur + outDur;
  }

  /**
   * Extract tension spine cycle duration (in + idle + out).
   * Center reels use in/idle/out, side reels use side_in/side_idle/side_out.
   * Both sets have identical durations — we use center as canonical.
   * Cached once — replaces runtime spine scanning in TensionReelController.
   */
  _extractTensionCycleDuration() {
    // Probe anticipation spine (replaces old tension spine)
    this._probeSpineDuration("anticipation", "in");

    const inDur = this._animationDurations.get("anticipation:in") ?? 0;
    const idleDur = this._animationDurations.get("anticipation:idle") ?? 0;
    const outDur = this._animationDurations.get("anticipation:out") ?? 0;
    const idleLoops = FEATURES_CONFIG.TENSION.IDLE_LOOPS ?? 1;
    const cycle = inDur + (idleDur * idleLoops) + outDur;
    this._cache.tensionCycle = cycle > 0 ? cycle : null;
  }

  /**
   * Extract master win and fast win durations from symbol spines.
   * Iterates all symbols in symbolData, finds the highest-priority symbol's
   * "win" and "win_fast" animation durations (same logic previously in useWinController).
   */
  _extractWinDurations() {
    const durationScale = FEATURES_CONFIG.WIN_ANIMATION_TIMING.DURATION_SCALE || 1;
    const fastAnimName = (FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_FAST?.ANIMATION_NAME || "win_fast").toLowerCase();

    let bestPriority = -1;
    let bestWinDuration = FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION;
    let bestFastDuration = null;

    // Deduplicate spines (multiple symbols can share the same spine file)
    const processedSpines = new Set();

    for (const [, symInfo] of Object.entries(symbolData)) {
      if (!symInfo?.spine || typeof symInfo.spine !== "string") continue;
      if (processedSpines.has(symInfo.spine)) continue;

      const spine = this._createSpine(symInfo.spine);
      if (!spine) continue;

      try {
        const animations = spine.skeleton?.data?.animations;
        if (!animations) continue;

        // Cache all animations from this spine
        for (const anim of animations) {
          const key = `${symInfo.spine}:${anim.name}`;
          if (!this._animationDurations.has(key)) {
            this._animationDurations.set(key, anim.duration * 1000);
          }
        }

        // Find "win" animation (exclude "win_fast")
        const winAnim = animations.find((a) =>
          a.name.toLowerCase().includes("win") && !a.name.toLowerCase().includes("win_fast"),
        );

        // Find "win_fast" animation
        const fastAnim = animations.find((a) =>
          a.name.toLowerCase() === fastAnimName,
        );

        // Cache per-spine win durations for per-symbol loop count lookups
        if (winAnim) {
          this._spineWinDurations.set(symInfo.spine, {
            win: winAnim.duration * 1000,
            winFast: fastAnim ? fastAnim.duration * 1000 : null,
          });
        }

        // Update master duration if this is the highest-priority symbol
        const priority = SpineAnimationData.SYMBOL_PRIORITY[symInfo.symbolPay] ?? 0;
        if (winAnim && priority >= bestPriority) {
          bestPriority = priority;
          bestWinDuration = winAnim.duration * 1000;
          bestFastDuration = fastAnim ? fastAnim.duration * 1000 : null;
        }

        processedSpines.add(symInfo.spine);
      } finally {
        spine.destroy({ children: true, texture: false, baseTexture: false });
      }
    }

    this._cache.masterWin = bestWinDuration * durationScale;
    this._cache.fastWin = bestFastDuration !== null
      ? bestFastDuration * durationScale
      : null;
  }

  /**
   * Probe a spine for a specific animation's duration
   * @param {string} spineName - Spine asset base name
   * @param {string} animationName - Exact animation name
   * @returns {number|null} Duration in ms
   */
  _probeSpineDuration(spineName, animationName) {
    const spine = this._createSpine(spineName);
    if (!spine) return null;

    try {
      const animations = spine.skeleton?.data?.animations;
      if (!animations) return null;

      // Cache all animations from this spine
      for (const anim of animations) {
        const key = `${spineName}:${anim.name}`;
        if (!this._animationDurations.has(key)) {
          this._animationDurations.set(key, anim.duration * 1000);
        }
      }

      const anim = animations.find(
        (a) => a.name === animationName || a.name.toLowerCase() === animationName.toLowerCase(),
      );
      return anim ? anim.duration * 1000 : null;
    } finally {
      spine.destroy({ children: true, texture: false, baseTexture: false });
    }
  }

  /**
   * Probe a spine for the first animation matching a prefix
   * @param {string} spineName - Spine asset base name
   * @param {string} prefix - Animation name prefix (e.g. "win_line_")
   * @returns {number|null} Duration in ms
   */
  _probeFirstMatching(spineName, prefix) {
    const spine = this._createSpine(spineName);
    if (!spine) return null;

    try {
      const animations = spine.skeleton?.data?.animations;
      if (!animations) return null;

      // Cache all animations from this spine
      for (const anim of animations) {
        const key = `${spineName}:${anim.name}`;
        if (!this._animationDurations.has(key)) {
          this._animationDurations.set(key, anim.duration * 1000);
        }
      }

      const match = animations.find((a) => a.name.startsWith(prefix));
      return match ? match.duration * 1000 : null;
    } finally {
      spine.destroy({ children: true, texture: false, baseTexture: false });
    }
  }

  /**
   * Create a temporary spine instance for probing
   * @param {string} spineName - Spine asset base name
   * @returns {Spine|null}
   */
  _createSpine(spineName) {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = `${spineName}-skeleton`;
      const atlasAlias = `${spineName}-atlas`;

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
        autoUpdate: false,
      });

      return spine?.skeleton?.data?.animations ? spine : null;
    } catch {
      return null;
    }
  }
}

export default SpineAnimationData;
